/**
 * `useRegistries(projectId)` - TanStack Query read hook for the
 * registry-enabled models in a single project.
 *
 * Implements the AC-1409 / AC-1377 P4.3 ticket and the Technical Design at
 * `docs/technical/AC-1377-react-query-technical-design.md`, sections 2.4
 * (key conventions), 4 (Phase 4 project resources), and 5.2 (read-hook
 * signatures).
 *
 * ## Why this exists
 *
 * Today, `RegistriesPage.tsx` calls `apiService.getRegistryEnabledModels`
 * inside a `useState` + `useEffect` block. That path has no caching, no
 * request deduplication, and remounting the page (e.g. after navigating
 * away and back) refetches from scratch every time. The same stale-modal
 * class of bug (AC-1390 / AC-1391 / AC-1394) the AC-1377 RQ migration
 * closes for workflows applies here once we start mutating the underlying
 * model rows; this hook gives the page a cached read path so that
 * `useModelMutations` (AC-1408) can invalidate `projectRegistries` and the
 * page picks up the new state without prop drilling.
 *
 * Cache key parity with `useModelMutations`: `useUpdateModel` /
 * `useDeleteModel` / `useCreateModel` already invalidate
 * `queryKeys.projectRegistries(projectId)` on success (TD section 2.6
 * dual-key invalidation). This hook is the consumer that picks up that
 * invalidation - without a cache slot at that key, the invalidation is a
 * no-op. Landing this hook closes the loop.
 *
 * ## Behavior
 *
 * - Fetches `apiService.getRegistryEnabledModels(projectId)` with cache
 *   key `queryKeys.projectRegistries(projectId)` (the AC-1396 factory;
 *   resolves to `['project', String(projectId), 'registries']`). The
 *   factory coerces `'7'` and `7` to the same array shape, so route-string
 *   IDs and store-numeric IDs share a single cache slot.
 *
 * - `enabled` is auto-disabled for empty IDs (`undefined`, `null`, `''`).
 *   Same rationale as `useAgents` / `useModels` / `useWorkflow`.
 *
 * - Caller-provided `options` flow through to `useQuery`. The caller's
 *   `enabled` is AND'ed with the internal id-truthy guard, so passing
 *   `{ enabled: false }` always wins.
 *
 * - Caller MUST NOT override `queryKey` or `queryFn` - those are owned by
 *   the hook. The `Options` type below excludes them.
 *
 * ## Argument forwarded to `apiService.getRegistryEnabledModels`
 *
 * Unlike `apiService.getAgents` / `apiService.getModels` which accept
 * string IDs and URL-interpolate, `apiService.getRegistryEnabledModels`
 * has a `number` signature. We coerce via `Number(projectId)` once the
 * truthy-id guard has cleared. The cache key factory uses `String(...)`
 * independently, so both forms produce the same key regardless of how the
 * call site supplies the ID.
 *
 * ## Why this hook does NOT subsume `useModels`
 *
 * `useModels` returns the FULL `Model[]` payload from
 * `GET /api/v1/projects/:projectId/models` and feeds the agent / step /
 * tool selection surfaces. `useRegistries` returns the SUBSET of models
 * with `registry_enabled = true` from
 * `GET /api/v1/projects/:projectId/models/registry-enabled`, with extra
 * fields (`entry_count`) the registries page renders. The two queries are
 * separate cache slots with different payload shapes; mutations on either
 * side are wired to invalidate BOTH (see `useModelMutations.ts`).
 */

import { useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import apiService from '../../services/api';
import { RegistryEnabledModel } from '../../types';
import { queryKeys } from './queryKeys';

/**
 * Caller-overridable subset of `UseQueryOptions`. We intentionally exclude
 * `queryKey` and `queryFn` because they are owned by this hook - letting
 * callers override them defeats the centralization the migration depends on.
 */
export type UseRegistriesOptions = Omit<
  UseQueryOptions<
    RegistryEnabledModel[],
    Error,
    RegistryEnabledModel[],
    ReturnType<typeof queryKeys.projectRegistries>
  >,
  'queryKey' | 'queryFn'
>;

/**
 * Returns the standard `UseQueryResult<RegistryEnabledModel[]>` from
 * TanStack Query.
 *
 * @param projectId Project ID. May be `string`, `number`, `undefined`, or
 *                  `null`. Falsy values disable the query (no fetch fires).
 * @param options   Optional `useQuery` options. `queryKey` and `queryFn` are
 *                  excluded from this type because they are owned by the hook.
 *
 * @example
 *   const { data: models, isLoading } = useRegistries(projectId);
 *   if (isLoading) return <Spinner />;
 *   return <RegistryList models={models ?? []} />;
 *
 * @example
 *   // Defer the fetch until a project is selected.
 *   const project = useCurrentProject();
 *   const registries = useRegistries(project?.project_id);
 */
export function useRegistries(
  projectId: string | number | undefined | null,
  options?: UseRegistriesOptions,
): UseQueryResult<RegistryEnabledModel[], Error> {
  // Treat `undefined`, `null`, and `''` as "no project selected yet".
  const hasId = projectId !== undefined && projectId !== null && projectId !== '';

  // The caller's `enabled` (if provided) is AND'ed with our internal
  // truthy-id guard. Either being falsy keeps the query disabled.
  const callerEnabled = options?.enabled;
  const enabled = hasId && (callerEnabled === undefined ? true : callerEnabled);

  return useQuery<
    RegistryEnabledModel[],
    Error,
    RegistryEnabledModel[],
    ReturnType<typeof queryKeys.projectRegistries>
  >({
    // Placeholder ID when `hasId` is false: RQ requires queryKey
    // unconditionally, but `enabled: false` guarantees the queryFn never
    // runs and the placeholder slot is never populated.
    queryKey: queryKeys.projectRegistries(hasId ? (projectId as string | number) : 0),
    // `apiService.getRegistryEnabledModels` is typed `(projectId: number)`,
    // unlike `getAgents` / `getModels` which accept string. Coerce via
    // `Number(...)` to match the signature.
    queryFn: () => apiService.getRegistryEnabledModels(Number(projectId)),
    ...options,
    enabled,
  });
}

export default useRegistries;
